import { useEffect, useState, useCallback } from "react";
import toast from "react-hot-toast";
import AppsWebHeader from "./AppsWebHeader";
import AppsWebTable from "./AppsWebTable";
import AppsWebForm from "./AppsWebForm";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, Pencil, X } from "lucide-react";
import propertyService from "../../Services/propertyService";
import { useAuth } from "../../Auth/AuthContext";
import PublisherApiService from "../../Services/PublisherApiService";

// Initial form state
const initialFormState = {
  name: "",
  type: "App",
  platform: "Android",
  appName: "",
  appLink: "",
  appId: "",
  websiteLink: "",
  status: "On approval",
  publisherId: 0,
};

export default function AppsWebPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "Admin";
  const [data, setData] = useState([]);
  const [publishers, setPublishers] = useState([]);
  const [form, setForm] = useState(initialFormState);
  const [modalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Memoized data fetching
  const fetchProperties = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = isAdmin
        ? await propertyService.getAll()
        : await propertyService.getByPublisher(user.publisherId);
      setData([...(result || [])].sort((a, b) => b.id - a.id));
    } catch (err) {
      toast.error("Failed to fetch properties");
    } finally {
      setIsLoading(false);
    }
  }, [isAdmin, user?.publisherId]);

  const loadPublishers = useCallback(async () => {
    try {
      const result = await PublisherApiService.getAllPublishers();
      setPublishers(result);
    } catch (error) {
      console.error("Failed to fetch publishers", error);
    }
  }, []);

  useEffect(() => {
    fetchProperties();
    loadPublishers();
  }, [fetchProperties, loadPublishers]);

  // Scroll lock effect
  useEffect(() => {
    document.body.style.overflow = modalOpen ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [modalOpen]);

  const openModal = () => setModalOpen(true);

  const closeModal = useCallback(() => {
    setForm(initialFormState);
    setIsEditing(false);
    setEditId(null);
    setModalOpen(false);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditing) {
       var response = await propertyService.update(editId, form);
        toast.success(response.message);
      } else {
       var response = await propertyService.create(form);
        toast.success(response.message);
      }
      fetchProperties();
      closeModal();
    } catch (err) {
      toast.error("Operation failed");
    }
  };

  const handleEdit = useCallback((row) => {
    setForm(row);
    setEditId(row.id);
    setIsEditing(true);
    setModalOpen(true);
  }, []);

  const handleDelete = async (row) => {
    if (!window.confirm("Are you sure to delete this property?")) return;
    
    try {
      await propertyService.delete(row.id);
      toast.success("Property deleted");
      fetchProperties();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  // Animation variants for better performance
  const modalBackdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.15 } },
    exit: { opacity: 0, transition: { duration: 0.15 } }
  };

  const modalContentVariants = {
    hidden: { opacity: 0, scale: 0.98, y: 8 },
    visible: { 
      opacity: 1, 
      scale: 1, 
      y: 0,
      transition: { 
        type: "spring",
        damping: 25,
        stiffness: 400,
        duration: 0.25
      }
    },
    exit: { 
      opacity: 0, 
      scale: 0.98, 
      y: 8,
      transition: { duration: 0.15 }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
      className="p-4 sm:p-6 space-y-4 sm:space-y-6"
    >
      <AppsWebHeader openModal={openModal} />
      
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <AppsWebTable 
          data={data || []} 
          onEdit={handleEdit} 
          onDelete={handleDelete} 
          isLoading={isLoading}
        />
      </motion.div>

      <AnimatePresence>
        {modalOpen && (
          <motion.div
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
            variants={modalBackdropVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <motion.div
              variants={modalContentVariants}
              className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden border border-gray-100"
            >
              {/* Decorative blobs */}
              <div className="absolute -right-20 -top-20 w-64 h-64 rounded-full bg-indigo-100/30 filter blur-3xl pointer-events-none" />
              <div className="absolute -left-10 -bottom-10 w-48 h-48 rounded-full bg-purple-100/20 filter blur-2xl pointer-events-none" />

              <div className="relative z-10">
                {/* Modal header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gradient-to-r from-white to-gray-50">
                  <div className="flex items-center gap-3">
                    <div className={`p-2.5 rounded-xl ${isEditing ? "bg-indigo-100" : "bg-purple-100"}`}>
                      {isEditing
                        ? <Pencil className="w-5 h-5 text-indigo-600" />
                        : <Globe className="w-5 h-5 text-purple-600" />}
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-gray-800">
                        {isEditing ? "Edit Property" : "Add New Property"}
                      </h2>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {isEditing ? "Update this property's details" : "Register a new app or website"}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={closeModal}
                    className="p-2 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-all"
                    aria-label="Close modal"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <AppsWebForm
                  form={form}
                  onChange={setForm}
                  onSubmit={handleSubmit}
                  onCancel={closeModal}
                  isEditing={isEditing}
                  publishers={publishers}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}